import React, { useState, Suspense, useCallback, useEffect } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Spinner } from 'baseui/spinner';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import type { User, BusinessUser, Request as RequestType } from '../../types/usersDemo';
import { getDrawerWidth } from './utils/helpers';
import { useUsersDemoDrawer } from './hooks/useUsersDemoDrawer';
import UserDrawer from './components/UserDrawer/UserDrawer';
import { IdModal } from './components/UserDrawer/IdModal';
import { mockUsers } from '../../data/mock/usersDemo';


// Lazy load tab components
const ResidentialTab = React.lazy(() => import('./components/ResidentialTab'));
const RequestTab = React.lazy(() => import('./components/RequestTab'));
const BusinessTab = React.lazy(() => import('./components/BusinessTab.tsx'));
// const ActivityTab = React.lazy(() => import('./components/ActivityTab'));

export type TabType = 'profile' | 'requests' | 'business' | 'support';
export type DrawerTabType = 'profile' | 'activity' | 'requests' | 'support';

// Drawer composition moved into components/UserDrawer/UserDrawer

// Styled components
const Container = styled('div', {
  backgroundColor: '#fff',
  minHeight: '100vh',
});

const Header = styled('div', {
  padding: '24px',
  borderBottom: '1px solid #E1E8ED',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const TabContainer = styled('div', {
  display: 'flex',
  borderBottom: '1px solid #E1E8ED',
  marginBottom: '24px',
  marginLeft: '20px',
  marginRight: '20px',
});

const Tab = styled('button', (props: { $active: boolean }) => ({
  padding: '12px 24px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontWeight: props.$active ? '600' : '400',
  color: props.$active ? '#000' : '#666',
  borderBottom: props.$active ? '2px solid #000' : '2px solid transparent',
  transition: 'all 0.2s ease',
  ':hover': {
    color: '#000',
  },
}));

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
});

// Drawer tabs now extracted to UserDrawer/Tabs.tsx


// helpers moved to ./utils/helpers


/**
 * Main UsersDemo component - Refactored for better maintainability
 * Each tab is now a separate component with its own state management
 */
export const UsersDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [globalRecordsPerPage] = useState(25);

  const drawer = useUsersDemoDrawer();
  const {
    isOpen,
    open,
    close,
    activeTab: drawerActiveTab,
    selectedContact,
    showIdModal,
    setShowIdModal,
    editingDrawerRequestId,
    setEditingDrawerRequestId,
    editingDrawerSupportId,
    setEditingDrawerSupportId,
    pendingChange: pendingDrawerStatusChange,
    setPendingChange: setPendingDrawerStatusChange,
    confirmPendingChange,
  } = drawer;


  const handleContactSelect = useCallback((contact: User | BusinessUser) => {
    // capture opener element if available
    if ((document as any).lastDrawerOpener) {
      drawer.setOpenerRef?.((document as any).lastDrawerOpener);
    }
    open(contact, 'profile');
  }, [open]);

  const handleDrawerClose = useCallback(() => {
    close();
  }, [close]);

  // n/a

  const handleDrawerStatusChangeConfirm = useCallback(async () => {
    await confirmPendingChange();
  }, [confirmPendingChange]);

  const handleRequestSelect = useCallback((request: RequestType) => {
    if ((document as any).lastDrawerOpener) {
      drawer.setOpenerRef?.((document as any).lastDrawerOpener);
    }
    // Try to enrich from mockUsers by matching name + lastName
    const match = mockUsers.find(
      (u) => u.name === (request as any).name && u.lastName === (request as any).lastName
    );

    const contact: User = match
      ? { ...(match as User) }
      : ({
          id: (request as any).id ?? Date.now(),
          name: (request as any).name ?? '',
          lastName: (request as any).lastName ?? '',
          email: (request as any).email ?? '',
          role: (request as any).role ?? 'Guest',
          status: (request as any).status ?? 'Pending',
          phone: (request as any).phone ?? '',
          estate: (request as any).estate ?? '',
          estateGroup: (request as any).estateGroup ?? '',
        } as User);

    open(contact, 'profile');
  }, [open]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingDrawerRequestId !== null || editingDrawerSupportId !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-status-dropdown]')) {
          setEditingDrawerRequestId(null);
          setEditingDrawerSupportId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDrawerRequestId, editingDrawerSupportId]);


  // Tab configuration
  const tabs = [
    { id: 'profile' as TabType, label: 'Residential', enabled: true },
    { id: 'requests' as TabType, label: 'Requests', enabled: true },
    { id: 'business' as TabType, label: 'Business', enabled: false },
    { id: 'support' as TabType, label: 'Support', enabled: false },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ResidentialTab recordsPerPage={globalRecordsPerPage} onUserClick={handleContactSelect} />;
      case 'requests':
        return <RequestTab recordsPerPage={globalRecordsPerPage} onRequestClick={handleRequestSelect as any} />;
      case 'business':
        return <BusinessTab recordsPerPage={globalRecordsPerPage} />;
      // case 'activities':
      //   return <ActivityTab recordsPerPage={globalRecordsPerPage} />;
      default:
        return <ResidentialTab recordsPerPage={globalRecordsPerPage} onUserClick={handleContactSelect} />;
    }
  };

  // Primary matching now inside PropertiesTab

  // Primary badge now computed inline in PropertiesTab; index no longer needed

  return (
    <Container>
      <Header>
        <div>
          <HeadingMedium margin={0}>The Beach</HeadingMedium>
          <p style={{ margin: '8px 0 0 0', color: '#6B7280' }}>
            Manage user profiles, activities, and access requests
          </p>
        </div>


      </Header>

      {/* Tab Navigation */}
      <TabContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => tab.enabled && setActiveTab(tab.id)}
            disabled={!tab.enabled}
            style={{
              opacity: tab.enabled ? 1 : 0.5,
              cursor: tab.enabled ? 'pointer' : 'not-allowed',
            }}
            title={tab.enabled ? undefined : `${tab.label} management is currently unavailable`}
          >
            {tab.label}
          </Tab>
        ))}
      </TabContainer>

      {/* Tab Content with Lazy Loading */}
      <div
        style={{
          marginRight: isOpen ? getDrawerWidth(drawerActiveTab) : 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
      <Suspense fallback={
        <LoadingContainer>
            <Spinner $size={40} />
        </LoadingContainer>
      }>
        {renderTabContent()}
      </Suspense>
      </div>

      <UserDrawer
        isOpen={isOpen}
        onClose={handleDrawerClose}
        contact={selectedContact}
        activeTab={drawerActiveTab}
        onTabChange={drawer.setActiveTab}
        editingRequestId={editingDrawerRequestId}
        setEditingRequestId={setEditingDrawerRequestId}
        editingSupportId={editingDrawerSupportId}
        setEditingSupportId={setEditingDrawerSupportId}
        setPendingChange={setPendingDrawerStatusChange as any}
        onOpenId={() => setShowIdModal(true)}
      />

      <IdModal isOpen={showIdModal} onClose={() => setShowIdModal(false)} />

      {/* Status Change Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!pendingDrawerStatusChange}
        onConfirm={handleDrawerStatusChangeConfirm}
        onCancel={() => setPendingDrawerStatusChange(null)}
      />
    </Container>
  );
};

export default UsersDemo;
